/**
 * Aviso de "algo se escribió en el backend", para que las vistas que no son dueñas del dato
 * puedan refrescarse solas.
 *
 * Lo dispara el interceptor de respuesta de `axiosInstance` ante cualquier request de escritura
 * (POST/PUT/PATCH/DELETE) que salga OK. Como el dataProvider de Refine usa **ese mismo**
 * `axiosInstance`, con un único punto quedan cubiertas tanto las llamadas directas (relaciones,
 * grupos, participaciones) como todo el CRUD de Refine (`useUpdate`, `useCreate`, `useDelete`).
 *
 * Por qué un bus y no props: el consumidor típico —el Historial— está a varios niveles de
 * distancia de donde se hacen las acciones (relations-card, groups-card, smart-select-contacts,
 * los paneles de presupuesto y pedido). Pasar un `onChanged` por props implicaría atravesar toda
 * esa jerarquía y, peor, acordarse de enchufarlo en cada acción nueva. Acá el emisor es uno y no
 * hay nada que recordar.
 *
 * Deliberadamente NO va por react-query/Refine invalidation: el Historial no usa los hooks de
 * datos de Refine (tiene su propio `useState` + keyset), así que no hay query que invalidar.
 */
export interface DataMutation {
  /** URL del request que escribió. */
  url: string;
  /** Método en minúscula: post | put | patch | delete. */
  method: string;
}

type Listener = (mutation: DataMutation) => void;

const listeners = new Set<Listener>();

/** Suscribe y devuelve la función para desuscribir (para el cleanup del useEffect). */
export function onDataMutation(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Avisa a los suscriptores. Se copia el Set antes de iterar porque un listener podría
 * desuscribirse durante el aviso, y cada uno se aísla en try/catch: que una vista falle al
 * refrescarse no puede romper la respuesta del request que la originó.
 */
export function notifyDataMutation(mutation: DataMutation): void {
  for (const listener of [...listeners]) {
    try {
      listener(mutation);
    } catch {
      /* un suscriptor roto no rompe al resto ni al request */
    }
  }
}
